/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

function debounce(fn, wait) {
    let timeout;
    return function debounced(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
    };
}

async function getCart() {
    const root = window.Shopify?.routes?.root ?? "/";
    const response = await fetch(`${root}cart.js`);
    return response.json();
}

async function updateCartAttributes(attributes) {
    const root = window.Shopify?.routes?.root ?? "/";
    const response = await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            attributes: attributes
        })
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description);
    return data;
}

class CartGiftOrder extends HTMLElement {
    #boundCartLoaded;
    #boundCheckboxChange;
    #boundUpdateMessage;
    #cartTextInput;
    #messageSection;
    #checkbox;
    connectedCallback() {
        this.#checkbox = this.querySelector(".cart-gift-order__checkbox");
        this.#messageSection = this.querySelector(".cart-gift-order__message");
        this.#cartTextInput = this.querySelector("cart-text-input");
        this.#boundCheckboxChange = this.#onCheckboxChange.bind(this);
        this.#boundUpdateMessage = debounce(this.#updateMessage.bind(this), 1e3);
        this.#syncFromCart();
        setTimeout((() => {
            this.#checkbox.addEventListener("change", this.#boundCheckboxChange);
            if (this.#cartTextInput) {
                const textarea = this.#cartTextInput.querySelector("textarea");
                if (textarea) textarea.addEventListener("input", this.#boundUpdateMessage);
            }
        }));
        this.#boundCartLoaded = this.#syncFromCart.bind(this);
        document.addEventListener("on:cart:loaded", this.#boundCartLoaded);
    }
    disconnectedCallback() {
        if (this.#checkbox) this.#checkbox.removeEventListener("change", this.#boundCheckboxChange);
        if (this.#cartTextInput) {
            const textarea = this.#cartTextInput.querySelector("textarea");
            if (textarea) textarea.removeEventListener("input", this.#boundUpdateMessage);
        }
        document.removeEventListener("on:cart:loaded", this.#boundCartLoaded);
    }
    async #syncFromCart() {
        try {
            const cart = await getCart();
            const isGiftOrder = cart.attributes["gift-order"] === "true";
            this.#checkbox.checked = isGiftOrder;
            this.#toggleMessage(isGiftOrder);
            if (this.#cartTextInput) {
                const message = cart.attributes["gift-message"] || "";
                const textarea = this.#cartTextInput.querySelector("textarea");
                if (textarea) textarea.value = message;
            }
        } catch (error) {
            console.log(error);
        }
    }
    #toggleMessage(show) {
        if (!this.#messageSection) return;
        if (show) {
            this.#messageSection.removeAttribute("hidden");
        } else {
            this.#messageSection.setAttribute("hidden", "");
        }
    }
    async #onCheckboxChange({target: {checked: checked}}) {
        this.#toggleMessage(checked);
        try {
            await updateCartAttributes({
                "gift-order": checked ? "true" : "false",
                ...checked ? {} : {
                    "gift-message": ""
                }
            });
            if (!checked && this.#cartTextInput) {
                const textarea = this.#cartTextInput.querySelector("textarea");
                if (textarea) textarea.value = "";
            }
        } catch (error) {
            console.log(error);
        }
    }
    async #updateMessage(e) {
        if (this.#cartTextInput) this.#cartTextInput.updating = true;
        try {
            await updateCartAttributes({
                "gift-message": e.target.value
            });
            if (this.#cartTextInput) this.#cartTextInput.updated = true;
        } catch (error) {
            console.log(error);
        }
    }
}

if (!customElements.get("cart-gift-order")) {
    customElements.define("cart-gift-order", CartGiftOrder);
}

/*-----------------------------------------------------------------------------/
/ Cart drawer discount code
/-----------------------------------------------------------------------------*/

async function getCartStore() {
    const {default: Cart} = await import("cart-store");
    return Cart;
}

async function updateCartDiscountCodes(codes) {
    const root = window.Shopify?.routes?.root ?? "/";
    const response = await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            discount: codes.join(",")
        })
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description);
    return data;
}

function getAppliedDiscountCodes(cart) {
    return (cart?.discount_codes || []).filter((d => d.applicable)).map((d => d.code));
}

class CartDiscountForm extends HTMLElement {
    #boundCodesClick;
    #boundSubmit;
    #button;
    #codes;
    #error;
    #form;
    #input;
    #unsubscribe;
    connectedCallback() {
        this.#form = this.querySelector("form");
        this.#input = this.querySelector(".cart-discount-form__input");
        this.#button = this.querySelector(".cart-discount-form__button");
        this.#error = this.querySelector(".cart-discount-form__error");
        this.#codes = this.querySelector(".cart-discount-form__codes");
        this.#boundSubmit = this.#onSubmit.bind(this);
        this.#boundCodesClick = this.#onCodesClick.bind(this);
        this.#form.addEventListener("submit", this.#boundSubmit);
        this.#codes.addEventListener("click", this.#boundCodesClick);
        this.#subscribe();
    }
    disconnectedCallback() {
        this.#form.removeEventListener("submit", this.#boundSubmit);
        this.#codes.removeEventListener("click", this.#boundCodesClick);
        if (this.#unsubscribe) this.#unsubscribe();
        this.#unsubscribe = null;
    }
    async #subscribe() {
        try {
            const Cart = await getCartStore();
            if (!this.isConnected) return;
            this.#renderCodes(Cart.getState().cart);
            this.#unsubscribe = Cart.subscribe((state => this.#renderCodes(state.cart)));
        } catch (error) {
            console.log(error);
        }
    }
    #renderCodes(cart) {
        // The initial #cart-data JSON has no discount_codes - keep the
        // server-rendered chips until the store holds a /cart.js response.
        if (!cart || !("discount_codes" in cart)) return;
        const removeLabel = this.dataset.removeLabel || "";
        const closeIcon = this.querySelector(".cart-discount-form__remove-icon")?.innerHTML ?? "&times;";
        const codeIcon = this.querySelector(".cart-discount-form__code-icon")?.innerHTML ?? "";
        this.#codes.replaceChildren(...getAppliedDiscountCodes(cart).map((code => {
            const item = document.createElement("li");
            item.className = "cart-discount-form__code";
            item.innerHTML = codeIcon;
            const label = document.createElement("span");
            label.className = "cart-discount-form__code-text";
            label.textContent = code;
            const button = document.createElement("button");
            button.type = "button";
            button.className = "cart-discount-form__remove";
            button.dataset.code = code;
            button.setAttribute("aria-label", `${removeLabel} ${code}`);
            button.innerHTML = closeIcon;
            item.append(label, button);
            return item;
        })));
    }
    async #onSubmit(e) {
        e.preventDefault();
        const code = this.#input.value.trim();
        if (!code) return;
        const Cart = await getCartStore();
        this.#setLoading(true);
        this.#showError("");
        try {
            const appliedCodes = getAppliedDiscountCodes(await getCart());
            if (appliedCodes.some((c => c.toLowerCase() === code.toLowerCase()))) {
                this.#input.value = "";
                this.#setLoading(false);
                return;
            }
            const cart = await updateCartDiscountCodes([ ...appliedCodes, code ]);
            const rejected = (cart.discount_codes || []).some((d => d.code.toLowerCase() === code.toLowerCase() && !d.applicable));
            if (rejected) {
                // Shopify keeps an unusable code on the cart, so drop it again.
                await updateCartDiscountCodes(appliedCodes);
                this.#showError(this.dataset.errorMessage);
            } else {
                this.#input.value = "";
            }
        } catch (error) {
            console.log(error);
            this.#showError(this.dataset.errorMessage);
        }
        await this.#refreshCart(Cart);
        this.#setLoading(false);
    }
    async #onCodesClick(e) {
        const button = e.target.closest(".cart-discount-form__remove");
        if (!button) return;
        const Cart = await getCartStore();
        this.#setLoading(true);
        this.#showError("");
        try {
            const remainingCodes = getAppliedDiscountCodes(await getCart()).filter((c => c !== button.dataset.code));
            await updateCartDiscountCodes(remainingCodes);
        } catch (error) {
            console.log(error);
        }
        await this.#refreshCart(Cart);
        this.#setLoading(false);
    }
    async #refreshCart(Cart) {
        try {
            await Cart.refresh();
        } catch (error) {
            console.log(error);
        }
    }
    #setLoading(loading) {
        this.classList.toggle("is-loading", loading);
        this.#button.disabled = loading;
        this.#input.disabled = loading;
        this.#codes.querySelectorAll("button").forEach((b => {
            b.disabled = loading;
        }));
    }
    #showError(message) {
        this.#error.textContent = message || "";
        this.#error.hidden = !message;
    }
}

if (!customElements.get("cart-discount-form")) {
    customElements.define("cart-discount-form", CartDiscountForm);
}

/*-----------------------------------------------------------------------------/
/ Cart store patches
/
/ Applied on top of cart-store.js so the vendor files stay untouched and survive
/ theme updates. This runs before cart-components.js registers the cart
/ elements - see the script order in layout/theme.liquid.
/-----------------------------------------------------------------------------*/

(async function patchCartStore() {
    const CART_UNAVAILABLE_MESSAGE = "Sorry, your cart could not be updated. Please refresh the page and try again.";
    const RETRIES = 2;
    const RETRY_DELAY = 400;

    let CartStore;
    try {
        ({CartStore: CartStore} = await import("cart-store"));
    } catch (e) {
        return; // No cart store on this page (password, gift card), nothing to patch.
    }

    // /cart.js normally answers with JSON, but it can answer with an HTML page
    // instead - request throttling, an expired theme preview session, a
    // challenge page. The stock getCart() feeds that straight into .json(), so
    // the shopper sees 'Could not get cart data: "SyntaxError: Unexpected token
    // <"' next to a line item. Retry, then fail with a readable message and
    // keep the technical detail in the console.
    CartStore.getCart = async function getCart() {
        const root = window.Shopify?.routes?.root ?? "/";
        let lastError = null;
        for (let attempt = 0; attempt <= RETRIES; attempt += 1) {
            if (attempt > 0) {
                await new Promise((resolve => {
                    setTimeout(resolve, RETRY_DELAY * attempt);
                }));
            }
            try {
                const response = await fetch(`${root}cart.js`);
                const body = await response.text();
                try {
                    return JSON.parse(body);
                } catch (parseError) {
                    lastError = new Error(`${root}cart.js returned ${response.status} "${response.headers.get("content-type") || "unknown content type"}": ${body.slice(0, 120)}`);
                }
            } catch (fetchError) {
                if (fetchError.name === "AbortError") throw fetchError;
                lastError = fetchError;
            }
        }
        console.error(lastError);
        throw new Error(CART_UNAVAILABLE_MESSAGE);
    };

    // Store subscribers run synchronously inside the add/change/update actions,
    // so an exception thrown while a component re-renders rejects the whole
    // cart operation. That is how a stale cart-item reading newItem.quantity
    // surfaces as "Cannot read properties of undefined (reading 'quantity')" on
    // both the product page and the cart drawer. Keep render failures in the
    // console instead of breaking add to cart.
    const originalSubscribe = CartStore.prototype.subscribe;
    CartStore.prototype.subscribe = function subscribe(callback) {
        if (typeof callback !== "function") return originalSubscribe.call(this, callback);
        return originalSubscribe.call(this, function safeCallback(state, prevState) {
            try {
                return callback.call(this, state, prevState);
            } catch (error) {
                console.error("Cart subscriber failed to render:", error);
                return undefined;
            }
        });
    };
})();
